import React, { useCallback, useEffect, useRef } from "react";
import { screenToScene } from "../../../features/canvas/geometry/viewportTransform";
import { attachStageToEngine } from "./stageWiring";

const STAGE_CLASS =
  "canvas-workspace-grid absolute inset-0 touch-none select-none overflow-hidden";
const LAYER_CLASS = "pointer-events-none absolute inset-0 h-full w-full";
const STAGE_LABEL = "Native canvas board";

const blurActiveEditable = () => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && activeElement !== document.body) {
    activeElement.blur();
  }
};

/**
 * The two stacked canvas layers plus pointer wiring. All rendering happens
 * imperatively through the engine's rAF scheduler — this component never
 * re-renders during gestures.
 * @param {{ engine: object }} props
 * @returns {JSX.Element}
 */
const CanvasStage = ({ engine }) => {
  const containerRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const staticCanvasRef = useRef(null);
  const wiringRef = useRef(null);

  useEffect(() => {
    wiringRef.current = attachStageToEngine({
      container: containerRef.current,
      engine,
      overlayCanvas: overlayCanvasRef.current,
      staticCanvas: staticCanvasRef.current,
    });
    return () => {
      wiringRef.current?.detach();
      wiringRef.current = null;
    };
  }, [engine]);

  const createPointerPayload = useCallback(
    (event) => {
      const screenPoint = wiringRef.current?.getScreenPoint(event) ?? {
        x: event.clientX,
        y: event.clientY,
      };

      return {
        button: event.button,
        metaKey: event.metaKey || event.ctrlKey,
        point: screenToScene({
          point: screenPoint,
          viewport: engine.getState().viewport,
        }),
        screenPoint,
        shiftKey: event.shiftKey,
      };
    },
    [engine]
  );

  const handlePointerDown = useCallback(
    (event) => {
      // Canceling pointerdown suppresses the browser's mousedown focus
      // change, which would otherwise blur (and instantly close) a text
      // editor opened during this event. Previously-focused inputs still
      // need their blur, so release focus explicitly.
      event.preventDefault();
      blurActiveEditable();
      wiringRef.current?.refreshRect();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      engine.pointer.handlePointerDown(createPointerPayload(event));
    },
    [createPointerPayload, engine]
  );

  const handlePointerMove = useCallback(
    (event) => engine.pointer.handlePointerMove(createPointerPayload(event)),
    [createPointerPayload, engine]
  );

  const handlePointerUp = useCallback(
    (event) => {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      engine.pointer.handlePointerUp(createPointerPayload(event));
    },
    [createPointerPayload, engine]
  );

  const handleDoubleClick = useCallback(
    (event) => engine.pointer.handleDoubleClick(createPointerPayload(event)),
    [createPointerPayload, engine]
  );

  return (
    <div
      aria-label={STAGE_LABEL}
      className={STAGE_CLASS}
      data-testid="native-canvas-board"
      onDoubleClick={handleDoubleClick}
      onPointerCancel={handlePointerUp}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      ref={containerRef}
      role="application"
    >
      <canvas className={LAYER_CLASS} ref={staticCanvasRef} />
      <canvas className={LAYER_CLASS} ref={overlayCanvasRef} />
    </div>
  );
};

export default CanvasStage;
